import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { avisoMapaListo } from '../lib/temasCopys';
// TANDA 26b (#236): el diff que produce los avisos de tema y la cola de la casa
// que los saca. Son LOS MISMOS que usa `GroupBankTemasPage` — no una copia: si
// por milisegundos la pantalla y el vigilante disparan a la vez, sonner
// deduplica por `id`, y el `id` es la clave que `avisosDeTransicion` calcula.
import { FINALES, avisosDeTransicion } from '../lib/bannersDeTema';
import { sacarBanners } from '../lib/bannerDeLaCasa';

/* PIPE-INST T4.5 (firma #120) — EL AVISO GLOBAL «TU DOCUMENTO YA ESTÁ LISTO».
 *
 * ── POR QUÉ ESTE AVISO SE SALE DE SU PANTALLA, Y SÓLO ÉSTE ──────────────────
 *
 * La doctrina del carril es que todos los avisos viven en la pantalla del
 * documento: el profesor sale, vuelve, y el avance está ahí. Funciona para la
 * GENERACIÓN, que el profesor dispara y se queda esperando. No funciona para la
 * LECTURA del documento, que tarda minutos y que nadie se queda mirando: sube el
 * archivo y se va. El único momento del carril en el que hay algo que decir y el
 * profesor no está donde se dice es justo ése — y por eso es el único que sale.
 *
 * ── DÓNDE VIVE, Y POR QUÉ NO DENTRO DE `AppLayout` ─────────────────────────
 *
 * Hermano de `NotificationListener`, dentro del router y por encima de las
 * rutas. Empezó dentro de `AppLayout` —el chasis autenticado, el sitio que
 * PILOTO-1B firmó para esta forma exacta de componente— y hubo que subirlo: el
 * logo del header navega a «/», que está FUERA de `AppLayout`, así que un toque
 * al logo desmontaba el vigilante y se llevaba consigo la memoria de qué
 * documentos estaba mirando. Lo que terminara mientras tanto no se anunciaba
 * nunca. Aquí arriba sobrevive a toda la navegación, que es justo lo que lo hace
 * global — y sigue sin costar nada donde no aplica: sin bandera institucional
 * devuelve `null` y no pide ni una vez, así que en landing y login no existe.
 *
 * ── LO QUE NO HACE ──────────────────────────────────────────────────────────
 *
 *   · No sondea si la cuenta no trae bandera institucional. Ni una llamada.
 *   · No sondea si no hay nada leyéndose. Arranca una vez para averiguarlo y,
 *     si el banco está quieto, se apaga hasta que alguien suba algo.
 *   · No avisa dos veces del mismo documento en la misma sesión.
 *   · No avisa de lo que ya estaba listo cuando llegó: el aviso es de una
 *     TRANSICIÓN. Anunciar al entrar todo lo que terminó ayer sería ruido.
 *   · No hay websockets, ni push, ni correo, ni cola de eventos. Un sondeo que
 *     se apaga solo.
 *
 * ── TANDA 26b (firma #236) — Y AHORA TAMBIÉN «TU TEMA YA TERMINÓ» ───────────
 *
 * El mismo agujero, un piso más abajo. Los avisos de tema los producía el diff
 * de `GroupBankTemasPage` comparando su `fotoPrevia` con la respuesta del
 * sondeo, y esa ref muere con la pantalla: el profesor que dispara una
 * generación y se va —que es lo que el propio copy de «Empezamos a generar» le
 * invita a hacer— no se enteraba de nada. Generar un tema tarda minutos: no
 * quedarse mirando es el caso NORMAL, no la excepción.
 *
 * LO QUE NO CAMBIA CON ESTO: la pantalla sigue haciendo su diff mientras está
 * montada, con la misma lógica y las mismas claves. No se sustituye nada — se
 * añade una segunda superficie para cuando la primera no existe. (La T26b las
 * callaba entre ellas con un silencio de ruta; la TANDA 27 / #239 lo retiró
 * — el porqué, junto a los silencios muertos, más abajo.)
 *
 * LO QUE TAMPOCO CAMBIA: no se persiste nada. Un tema apuntado antes de un F5 se
 * pierde, exactamente igual que el `ready_toast` del documento. Guardarlo sería
 * estrenar almacenamiento y una caducidad que hoy nadie necesita.
 */

//: El ritmo. Más lento que los dos sondeos de pantalla (4 s y 5 s) a propósito:
//: éste corre en TODA la app y no tiene a nadie mirándolo, así que su trabajo es
//: no perderse el final, no cronometrarlo.
export const VIGILANCIA_MS = 8000;

/** El evento con el que una subida despierta al vigilante sin que el chasis
 *  tenga que conocer a la sección del banco. */
export const DOC_DEL_BANCO_SUBIDO = 'studybuddy:pi-doc-subido';

/** TANDA 26b (#236) — el evento hermano, para los TEMAS. Misma doctrina que el
 *  de arriba: va por `window` y no por import, así que la pantalla de temas no
 *  tiene que conocer al chasis ni el chasis a la pantalla. Éste sí lleva carga
 *  —`CustomEvent` con `detail`— porque un tema no se puede descubrir sondeando
 *  una lista: hay que decir CUÁL, de qué documento y cómo se llama. */
export const TEMA_DEL_BANCO_ENCOLADO = 'studybuddy:pi-tema-encolado';

/**
 * La función hermana de `window.dispatchEvent(new Event(DOC_DEL_BANCO_SUBIDO))`.
 *
 * Es función y no un `dispatchEvent` a pelo por lo que el evento de documentos
 * no necesitaba: aquí hay una FORMA de carga que las dos puntas tienen que
 * compartir, y dejarla escrita en el sitio que la lee evita que la pantalla y el
 * vigilante se desincronicen en silencio el día que cambie un campo.
 *
 * @param {{groupId: string, docId: string, temaId: string, nombre: string}} tema
 */
export function registrarTemaEncolado(tema) {
  if (!tema?.groupId || !tema?.docId || !tema?.temaId) return;
  window.dispatchEvent(new CustomEvent(TEMA_DEL_BANCO_ENCOLADO, { detail: tema }));
}

/** La llave del registro de temas: un documento concreto de un grupo concreto,
 *  que es la unidad que se sondea (una petición por documento, no por tema). */
const llaveDeDoc = (groupId, docId) => `${groupId}:${docId}`;

//: Tope de ids por petición, el MISMO que el servidor aplica. Se recorta también
//: aquí para que el comportamiento sea predecible en vez de depender de dónde se
//: corte la lista: lo que está PROCESANDO vuelve siempre (el servidor lo añade
//: por su cuenta), así que el recorte sólo puede afectar a un final que ya
//: ocurrió, y el siguiente ciclo lo alcanza igual.
const MAX_VIGILADOS = 40;

/* TANDA 27 (firma #239) — AQUÍ VIVÍAN LOS DOS SILENCIOS DE RUTA, Y SE FUERON.
 *
 * Eran `enEsaPantalla(doc)` —callaba el aviso de DOCUMENTO en la pantalla del
 * grupo o en la del propio documento— y `enPantallaDeTemas(g, d)` —callaba el de
 * TEMA en la pantalla de temas de ese documento—. Los dos leían
 * `window.location.pathname` y los dos partían de la misma premisa: «si el
 * profesor ya lo tiene delante, la card cambia sola y el toast sobra».
 *
 * LA PREMISA ES FALSA POR DOS MOTIVOS, y la firma los zanja:
 *
 *   1. Estar EN una pantalla no es estar MIRANDO ese renglón. La card que muta
 *      está donde estaba, en una lista que puede ser larga y estar scrolleada en
 *      otro sitio. El cambio silencioso no se ve.
 *   2. El silencio es una apuesta que no se puede perder dos veces. Si el
 *      vigilante calla Y la pantalla tampoco pinta —porque su diff depende de
 *      una ref que puede nacer vacía, que es exactamente el bug que arregló la
 *      TANDA 26b— la noticia se pierde entera. Hablar de más cuesta un toast;
 *      callar de más cuesta la noticia.
 *
 * POR QUÉ ESTO NO DUPLICA NADA:
 *
 *   · TEMAS: los dos productores —la pantalla con su `fotoPrevia` y el vigilante
 *     con su registro— pasan por la MISMA `claveDeAviso` y la entregan a sonner
 *     como `id`. Dos avisos con el mismo `id` son UN toast (el segundo reemplaza
 *     al primero, no se apila). Lo fija `GroupBankVigilante.t27.test.js`.
 *   · DOCUMENTO: no hay con quién duplicarse. `avisoMapaListo` tiene un único
 *     llamador en todo el frontend, que es la línea de abajo.
 *
 * LO QUE NO SE FUE, Y NO ES LO MISMO: el silencio de `d.pi_sin_temas`. Ése no es
 * de ruta sino de CONTENIDO — manda a «elegir temas» a un documento que no tiene
 * ninguno, y eso abre una pantalla vacía esté el profesor donde esté.
 */


export default function GroupBankVigilante() {
  const { user } = useAuth();
  const institucional = !!user?.is_institutional;
  const { t } = useTranslation('dashboard');

  /* TANDA 26 (firma #234) — EL `t` VA EN UNA REF, Y NO ES UN ATAJO.
   *
   * Este componente devuelve `null` y todo su estado vive en refs; su único
   * efecto monta un bucle de sondeo con `[institucional]` como dependencia.
   * Meter `t` en esa lista haría que CADA CAMBIO DE IDIOMA desmontara el bucle
   * —limpiando su temporizador y su `enVuelo`— y arrancara otro: el sondeo
   * perdería su sitio y los ids ya vigilados se reencolarían.
   *
   * Con la ref, el efecto conserva su identidad EXACTA (misma lista de
   * dependencias que antes de esta tanda) y el toast se traduce con el idioma
   * que esté puesto EN EL INSTANTE EN QUE SALE, que además es lo correcto: un
   * aviso que se dispara diez minutos después debe hablar el idioma de ahora,
   * no el de cuando se montó el chasis.
   *
   * Lo que sí estrena el componente es un RE-RENDER por cambio de idioma, que
   * `useTranslation` provoca. Es inocuo: no pinta nada. */
  const tRef = useRef(t);
  tRef.current = t;

  // Los tres registros del vigilante, en refs porque ninguno pinta nada: no
  // tiene sentido re-renderizar un componente que devuelve `null`.
  const vigilados = useRef(new Set());   // ids en `processing` que estamos mirando
  const anunciados = useRef(new Set());  // ids ya avisados en esta sesión
  const despierto = useRef(0);           // tics de espera pedidos desde fuera
  /* TANDA 26b (#236) — el cuarto registro: los TEMAS encolados.
     `Map<"grupo:doc", {groupId, docId, temas: Map<temaId, {nombre, estado}>}>`.
     `estado` arranca en `null` y guarda la última foto vista de ESE tema: es el
     otro lado del diff, exactamente como la `fotoPrevia` de la pantalla. */
  const temasVigilados = useRef(new Map());
  /* El arranque ya ocurrió. Sólo lo lee la compuerta de `mirar()` — ver ahí. */
  const arranqueHecho = useRef(false);

  useEffect(() => {
    if (!institucional) return undefined;

    let vivo = true;
    let timer = null;
    // «Hay un tic EN VUELO». Sin esto, un evento de subida que cayera mientras
    // `mirar()` espera su respuesta veria `timer === null` —porque el tic en
    // vuelo todavia no ha agendado el siguiente— y arrancaria un SEGUNDO bucle
    // en paralelo: dos temporizadores pidiendo lo mismo para siempre.
    let enVuelo = false;

    const mirar = async () => {
      /* TANDA 26b (#236) — LA COMPUERTA QUE EVITA UNA PETICIÓN DE MÁS.
         Desde esta tanda el bucle también late por TEMAS, y sin esto cada latido
         de un tema arrastraría una lectura del endpoint de DOCUMENTOS que no
         tiene a nadie esperándola: una generación de temas dura minutos, o sea
         decenas de peticiones inútiles.
         NO CAMBIA NADA DE LO QUE YA HABÍA: antes de esta tanda `mirar()` sólo se
         invocaba en el arranque, con `vigilados` lleno, o con una espera pedida
         desde fuera (`despierto`) — nunca en el estado que esta línea corta. */
      if (arranqueHecho.current && vigilados.current.size === 0
          && despierto.current === 0) return false;
      arranqueHecho.current = true;
      let datos;
      try {
        const ids = [...vigilados.current].slice(0, MAX_VIGILADOS);
        const res = await api.get('/groups/bank/vigilancia'
          + (ids.length ? `?ids=${ids.map(encodeURIComponent).join(',')}` : ''));
        datos = res.data?.documentos || [];
      } catch {
        // Una lectura fallida no apaga el vigilante ni inventa un aviso: se
        // vuelve a intentar en el siguiente tic con lo que ya sabíamos.
        //
        // PERO SÓLO SI SABÍAMOS ALGO. Devolver `true` a secas convertía un fallo
        // de red en el arranque —cuando todavía no hemos averiguado si hay algo
        // leyéndose— en un sondeo cada ocho segundos PARA SIEMPRE, sobre un banco
        // que a lo mejor está vacío. Si ya estábamos vigilando documentos, esos
        // documentos siguen ahí y hay que reintentar; si no, se calla, y una
        // subida o la siguiente carga de la app lo vuelven a despertar.
        return vigilados.current.size > 0;
      }
      if (!vivo) return false;

      const procesando = new Set();
      datos.forEach(d => {
        if (d.status === 'processing') { procesando.add(d.id); return; }
        // LA TRANSICIÓN, Y SÓLO LA TRANSICIÓN: se avisa del documento que
        // ESTÁBAMOS viendo procesar y ahora está listo. Un documento que ya
        // llegó listo a la primera vuelta nunca estuvo en `vigilados`, así que
        // no dispara nada.
        if (d.status === 'ready' && vigilados.current.has(d.id)
            && !anunciados.current.has(d.id)) {
          anunciados.current.add(d.id);
          // EL ÚNICO SILENCIO QUE QUEDA (TANDA 27, #239: el de ruta se fue —ver
          // el bloque de arriba—): SI NO HAY TEMAS QUE ELEGIR, no se manda a
          // elegirlos. El copy dice «tócalo para elegir temas» y ese documento
          // abre una pantalla vacía: es el mismo viaje inútil que la card de la
          // lista existe para ahorrar, y ahí ya lo dice con su propio copy.
          //
          // El documento se marca como anunciado igual: la noticia llegó, sólo
          // que por la superficie que le tocaba.
          if (!d.pi_sin_temas) {
            toast.success(avisoMapaListo(tRef.current, d.filename || '', d.grupo || ''),
              { duration: 10000 });
          }
        }
      });
      vigilados.current = procesando;
      return procesando.size > 0;
    };

    /* ── TANDA 26b (#236) — EL SONDEO DE LOS TEMAS ─────────────────────────────
     *
     * UNA PETICIÓN POR DOCUMENTO CON TEMAS REGISTRADOS, al MISMO endpoint que la
     * pantalla de temas ya usa (`GET /groups/:g/bank/:d/temas`). No se estrena
     * ruta: la que hay devuelve la foto entera, que es exactamente el otro lado
     * del diff. Sin temas registrados no pide nada — ni una llamada.
     *
     * Devuelve si queda algo que mirar, que es lo que mantiene vivo el bucle.
     */
    const mirarTemas = async () => {
      if (temasVigilados.current.size === 0) return false;
      // Se recorre una copia: el bucle borra entradas del registro, y una subida
      // o un disparo nuevo pueden añadirlas mientras esperamos una respuesta.
      for (const [llave, registro] of [...temasVigilados.current.entries()]) {
        let temas;
        try {
          const res = await api.get(
            `/groups/${registro.groupId}/bank/${registro.docId}/temas`);
          temas = res.data?.temas || [];
        } catch (err) {
          // UN 404/403 ES DEFINITIVO: el documento se borró, o el grupo dejó de
          // ser suyo. Seguir preguntando por él sería un sondeo eterno sobre algo
          // que ya no existe. Cualquier otro fallo (red, 5xx) se reintenta en el
          // siguiente tic con lo que ya sabíamos, igual que el de documentos.
          const codigo = err?.response?.status;
          if (codigo === 404 || codigo === 403) temasVigilados.current.delete(llave);
          continue;
        }
        if (!vivo) return false;

        const registrados = temas.filter(tm => registro.temas.has(tm.tema_id));
        // LA FOTO ANTERIOR, ARMADA DEL REGISTRO. Sólo entran los temas de los que
        // YA vimos una foto: sin foto previa no hay transición que contar, que es
        // la misma regla que aplica la pantalla con su `fotoPrevia = null`.
        const anterior = registrados
          .filter(tm => registro.temas.get(tm.tema_id).estado !== null)
          .map(tm => ({ tema_id: tm.tema_id,
                        estado: registro.temas.get(tm.tema_id).estado }));
        // EL NOMBRE DEL REGISTRO ES EL RESPALDO, no el que manda: si la foto trae
        // el suyo, ése es el bueno (el profesor pudo renombrar el mapa). El del
        // registro cubre el hueco de una foto que no lo traiga, porque el copy lo
        // interpola sí o sí.
        const nuevo = registrados.map(tm => ({
          ...tm, nombre: tm.nombre || registro.temas.get(tm.tema_id).nombre }));

        // TANDA 27 (#239) — SIN SILENCIO DE RUTA. Si el profesor está en la
        // pantalla de temas de este documento, ella pinta los MISMOS avisos con
        // la MISMA clave (`claveDeAviso`), y `sacarBanners` la entrega a sonner
        // como `id`: los dos disparos se ven como UNO. Callar aquí sólo servía
        // para perder la noticia entera cuando el diff de la pantalla no la
        // tenía (una `fotoPrevia` recién nacida no produce transiciones).
        sacarBanners(avisosDeTransicion(tRef.current, anterior, nuevo));

        const vistos = new Set(registrados.map(tm => tm.tema_id));
        for (const tm of nuevo) {
          if (FINALES.includes(tm.estado)) registro.temas.delete(tm.tema_id);
          else registro.temas.set(tm.tema_id,
            { ...registro.temas.get(tm.tema_id), estado: tm.estado });
        }
        // UN TEMA QUE DESAPARECIÓ DE LA FOTO ya no se puede seguir: el mapa del
        // documento cambió debajo. Se suelta en vez de preguntarse para siempre.
        for (const temaId of [...registro.temas.keys()]) {
          if (!vistos.has(temaId)) registro.temas.delete(temaId);
        }
        if (registro.temas.size === 0) temasVigilados.current.delete(llave);
      }
      return temasVigilados.current.size > 0;
    };

    const tic = async () => {
      enVuelo = true;
      // LOS DOS SONDEOS EN SERIE Y NO EN PARALELO: son del mismo profesor y de la
      // misma app: dos peticiones a la vez no llegan antes, y en serie el tic
      // sigue siendo UNA unidad que `enVuelo` puede cubrir entera.
      const siguenDocs = await mirar();
      const siguenTemas = await mirarTemas();
      const sigue = siguenDocs || siguenTemas;
      enVuelo = false;
      if (!vivo) return;
      // Se sigue mirando mientras quede algo procesando, o mientras alguien haya
      // pedido explícitamente unas vueltas más tras una subida (el documento
      // recién subido tarda un instante en aparecer como `processing`).
      if (sigue) despierto.current = 0;
      else if (despierto.current > 0) despierto.current -= 1;
      if (sigue || despierto.current > 0) timer = setTimeout(tic, VIGILANCIA_MS);
      else timer = null;
    };

    const despertar = () => {
      despierto.current = 3;
      if (timer === null && !enVuelo) { timer = setTimeout(tic, VIGILANCIA_MS); }
    };
    window.addEventListener(DOC_DEL_BANCO_SUBIDO, despertar);

    /* TANDA 26b (#236) — APUNTAR UN TEMA Y, SI HACE FALTA, VOLVER A ENCENDER EL
       BUCLE. No usa `despertar()`: aquélla pide TRES vueltas de cortesía porque
       un documento recién subido tarda un instante en aparecer como `processing`.
       Aquí no hay nada que esperar —el tema ya existe, el 202 lo confirma— y el
       bucle se mantiene solo mientras el registro no esté vacío. */
    const apuntarTema = (ev) => {
      const { groupId, docId, temaId, nombre } = ev?.detail || {};
      if (!groupId || !docId || !temaId) return;
      const llave = llaveDeDoc(groupId, docId);
      if (!temasVigilados.current.has(llave)) {
        temasVigilados.current.set(llave, { groupId, docId, temas: new Map() });
      }
      // `estado: null` = «todavía no hemos visto su foto». Un tema re-encolado
      // vuelve a nacer sin foto previa, que es lo correcto: su corrida nueva es
      // otra corrida, con otra clave de aviso.
      temasVigilados.current.get(llave).temas.set(temaId, { nombre: nombre || '', estado: null });
      if (timer === null && !enVuelo) { timer = setTimeout(tic, VIGILANCIA_MS); }
    };
    window.addEventListener(TEMA_DEL_BANCO_ENCOLADO, apuntarTema);

    // EL ARRANQUE ES UNA SOLA LECTURA. Si el banco está quieto, el vigilante se
    // apaga aquí mismo y no vuelve a pedir nada hasta que alguien suba algo.
    tic();

    return () => {
      vivo = false;
      if (timer !== null) clearTimeout(timer);
      window.removeEventListener(DOC_DEL_BANCO_SUBIDO, despertar);
      window.removeEventListener(TEMA_DEL_BANCO_ENCOLADO, apuntarTema);
    };
  }, [institucional]);

  return null;
}
